import React, { useState, useEffect } from "react";
import { type NodeProps, type Node, useReactFlow } from "@xyflow/react";
import { Webhook, X } from "lucide-react";
import { WorkflowBaseNode } from "../../common/shared/WorkflowBaseNode";
import { WorkflowNodeModal } from "../../common/shared/WorkflowNodeModal";

export const WebhookNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const { setNodes } = useReactFlow();

  const [webhookUrl, setWebhookUrl] = useState<string>(data?.webhook_url || "");
  const [method, setMethod] = useState<string>(data?.method || "POST");
  const [webhookKey, setWebhookKey] = useState<string>(data?.webhook_key || "");
  const [webhookValue, setWebhookValue] = useState<string>(
    data?.webhook_value || "",
  );
  const [apiKeyFormat, setApiKeyFormat] = useState<string>(
    data?.api_key_format || "headers",
  );
  const [results, setResults] = useState<string>(data?.results || "");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const isConfigured = !!(webhookUrl && method && results);

  // ── Sync config to ReactFlow ──────────────────────────────────────
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                webhook_url: webhookUrl,
                method,
                webhook_key: webhookKey,
                webhook_value: webhookValue,
                api_key_format: apiKeyFormat,
                results,
              },
            }
          : node,
      ),
    );
  }, [webhookUrl, method, webhookKey, webhookValue, apiKeyFormat, results, id, setNodes]);

  // ── Drop handlers ─────────────────────────────────────────────────
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const variable = e.dataTransfer.getData("text/plain");
    if (variable) setResults(variable);
    setIsDraggingOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  return (
    <WorkflowBaseNode
      id={id}
      data={data}
      selected={selected}
      title="Webhook"
      icon={<Webhook className="w-4 h-4 text-amber-700" />}
      nodeDocsType="WEBHOOK"
      themeColor="amber"
      configureLabel="Configure Webhook"
      isConfigured={isConfigured}
      validationErrors={[
        ...(!webhookUrl ? ["Webhook URL is required"] : []),
        ...(!method ? ["Method is required"] : []),
        ...(!results ? ["Results field is required"] : []),
      ]}
      statusText={{
        running: "Executing...",
        success: "Executed",
        error: "Execution Failed",
      }}
      onConfigure={() => setIsModalOpen(true)}
    >
      <WorkflowNodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        nodeId={id}
        title="Webhook - Send data"
        icon={<Webhook className="w-5 h-5 text-amber-600" />}
        themeColor="amber"
        nodeDocsType="WEBHOOK"
        validationStatus={{
          isValid: isConfigured,
          errors: [
            ...(!webhookUrl ? ["Webhook URL is required"] : []),
            ...(!method ? ["Method is required"] : []),
            ...(!results ? ["Results field is required"] : []),
          ],
        }}
        saveDisabled={!isConfigured}
        nodeData={data}
        nodeIcon={<Webhook className="w-6 h-6 text-gray-400" />}
        emptyOutputsText="No outputs yet"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Webhook URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              className={`w-full px-3 py-2 bg-white border ${!webhookUrl ? "border-red-300" : "border-gray-300"} rounded-lg text-sm text-gray-700`}
            />
            {!webhookUrl && (
              <p className="text-xs text-red-500 mt-1">Webhook URL is required</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Method <span className="text-red-500">*</span>
            </label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700"
            >
              <option>POST</option>
              <option>GET</option>
              <option>PUT</option>
              <option>PATCH</option>
              <option>DELETE</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Webhook Key
            </label>
            <input
              type="text"
              value={webhookKey}
              onChange={(e) => setWebhookKey(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Webhook Value
            </label>
            <input
              type="text"
              value={webhookValue}
              onChange={(e) => setWebhookValue(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              API Key Format
            </label>
            <select
              value={apiKeyFormat}
              onChange={(e) => setApiKeyFormat(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700"
            >
              <option>headers</option>
              <option>query</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Results <span className="text-red-500">*</span>
            </label>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`min-h-[80px] px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-all ${
                isDraggingOver
                  ? "border-amber-400 bg-amber-50"
                  : results
                    ? "border-amber-300 bg-amber-50/50"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400"
              }`}
            >
              {results ? (
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      Selected field
                    </div>
                    <code className="text-xs text-amber-700 font-mono break-all">
                      {results}
                    </code>
                  </div>
                  <button
                    onClick={() => setResults("")}
                    className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white rounded transition-colors shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-400">
                  <span className="text-sm">Drag and drop results here</span>
                </div>
              )}
            </div>
            {!results && (
              <p className="text-xs text-red-500 mt-1">Results field is required</p>
            )}
          </div>
        </div>
      </WorkflowNodeModal>
    </WorkflowBaseNode>
  );
};

export default WebhookNode;
